import React, {createContext, ReactNode, useContext, useState} from 'react';

export interface BreadcrumbItem {
  label?: string;
  href?: string;
  isCurrentPage?: boolean;
  component?: ReactNode; // Allow custom React component
}

// Stats pill variant colors
export type StatVariant = 'default' | 'warning' | 'success' | 'muted';

export interface PageStat {
  value: string | number;
  label: string;
  variant?: StatVariant;
}

export interface PageHeaderConfig {
  title: string;
  subtitle?: string;
  stats?: PageStat[];
  actions?: ReactNode;
}

interface BreadcrumbContextType {
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
  hideHeader: boolean;
  setHideHeader: (hide: boolean) => void;
  pageHeader: PageHeaderConfig | null;
  setPageHeader: (config: PageHeaderConfig | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [hideHeader, setHideHeader] = useState(false);
  const [pageHeader, setPageHeader] = useState<PageHeaderConfig | null>(null);

  return (
    <BreadcrumbContext.Provider value={{
      breadcrumbs,
      setBreadcrumbs,
      hideHeader,
      setHideHeader,
      pageHeader,
      setPageHeader,
    }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbs() {
  const context = useContext(BreadcrumbContext);
  if (context === undefined) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider');
  }
  return context;
}
